import React, { useState } from 'react';
import { Text, ProgressBar, Button, CardSurface } from '@spark';
import EvaluationProcessingIcon from '@/shared/assets/icons/evaluation_processing.svg?react';
import '@/app/styles/scrollbar.css';

const EvaluationProcessingState = ({
    progress = 0,
    status = "evaluating",
    stepName = null,
    totalSteps = null,
    currentStep = null,
    errorDetails = null,
    initialProgress = 0,
    initialStepName = "Setting up evaluation environment",
    initialTotalSteps = 4,
    initialCurrentStep = 1
}) => {
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const resolvedProgress = typeof progress === "number" && Number.isFinite(progress) ? progress : 0;
    const resolvedInitialProgress =
        typeof initialProgress === "number" && Number.isFinite(initialProgress) ? initialProgress : 0;
    const progressPercentage = Math.min(Math.max(resolvedProgress || resolvedInitialProgress, 0), 100);

    const resolvedTotalSteps =
        typeof totalSteps === "number" && Number.isFinite(totalSteps) && totalSteps > 0
            ? totalSteps
            : initialTotalSteps;

    const resolvedCurrentStep =
        typeof currentStep === "number" && Number.isFinite(currentStep) && currentStep > 0
            ? currentStep
            : (resolvedProgress > 0
                ? Math.min(
                    resolvedTotalSteps,
                    Math.max(1, Math.ceil((progressPercentage / 100) * resolvedTotalSteps))
                )
                : initialCurrentStep);

    const resolvedStepName =
        typeof stepName === "string" && stepName.trim().length > 0
            ? stepName
            : (resolvedProgress > 0 ? null : initialStepName);

    return (
        <CardSurface
            className="fs-unmask"
            background="white"
            bordered
            padding="large"
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div style={{
                    textAlign: 'center',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--dimension-spacing-16)',
                }}>
                    <EvaluationProcessingIcon style={{ width: 120, height: 120 }} />

                    <Text variant="heading-5-semibold" color="primary">
                        {status === "completed"
                            ? "Almost completed..."
                            : "Evaluating your model..."}
                    </Text>

                    <Text variant="base-regular" color="secondary" style={{ maxWidth: 400 }}>
                        We're processing your data and analyzing performance. This might take a few minutes.
                    </Text>

                    <div style={{ width: '100%', maxWidth: 500 }}>
                        <ProgressBar
                            value={progressPercentage}
                            type={status === "failed" ? "error" : "default"}
                            showPercentage={false}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text variant="base-semibold" as="span">
                                Step {resolvedCurrentStep} of {resolvedTotalSteps}:
                            </Text>{' '}
                            <Text variant="base-regular" as="span">
                                {resolvedStepName || "Working…"}
                            </Text>
                            {errorDetails && (
                                <>
                                    {' - '}
                                    <Button
                                        variant="ghost"
                                        size="small"
                                        onClick={() => setDetailsExpanded(prev => !prev)}
                                        style={{ display: 'inline', padding: 0, minHeight: 'auto' }}
                                    >
                                        {detailsExpanded ? 'hide' : 'details'}
                                    </Button>
                                </>
                            )}
                        </div>
                        {detailsExpanded && errorDetails && (
                            <div
                                className="optune-scrollbar"
                                style={{
                                    marginTop: 12,
                                    padding: '10px 14px',
                                    background: 'var(--Semantic-Bg-error-light, #fef2f2)',
                                    border: '1px solid var(--Semantic-Border-error, #fecaca)',
                                    borderRadius: 6,
                                    maxHeight: 160,
                                    overflowY: 'auto',
                                    textAlign: 'left',
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                <Text variant="detail-regular" color="primary">
                                    {errorDetails}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </CardSurface>
    );
};

export default EvaluationProcessingState;
